import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {Button, Flex, Form, InputNumber, message, Table} from "antd";
import MyText from "../../../../../components/UI/Text/MyText";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import FormItem from "antd/es/form/FormItem";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import {getJournal} from "../../../../../store/slices/TeacherGroupSlice";
import JournalService from "../../../../../services/JournalService";
import GroupService from "../../../../../services/GroupService";
import {createStyles} from "antd-style";
import StudentScheduleModal from "../../../../../components/UI/StudentScheduleModal/StudentScheduleModal";

const useStyle = createStyles(({css, token}) => {
    const {antCls} = token;
    return {
        customTable: css`
            ${antCls}-table {
                ${antCls}-table-container {
                    ${antCls}-table-body,
                    ${antCls}-table-content {
                        scrollbar-width: thin;
                        scrollbar-color: #eaeaea transparent;
                        scrollbar-gutter: stable;
                    }
                }
            }
        `,
    };
});
const GradesPage = () => {
    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {journal, isLoading} = useSelector(state => state.teacherGroup);

    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);

    // п.10: модалка полного расписания ученика
    const [openSchedule, setOpenSchedule] = useState(false);
    const [scheduleStudent, setScheduleStudent] = useState(null);

    // п.6/п.7: даты присоединения учеников и порог низкой посещаемости
    const [joinDates, setJoinDates] = useState({});
    const [lowThreshold, setLowThreshold] = useState(50);

    useEffect(() => {
        if (!journal?.group?.id) return;
        GroupService.getGroupEnrollments(journal.group.id)
            .then((d) => {
                const map = {};
                (d?.enrollments || []).forEach(e => {
                    // п.6/п.7: храним joinDate как строку 'YYYY-MM-DD' и сравниваем по календарной дате,
                    // чтобы не было таймзонного off-by-one (LocalDate → UTC-полночь vs LocalDateTime → локальная).
                    if (e.joinDate) map[e.studentId] = String(e.joinDate).slice(0, 10);
                });
                setJoinDates(map);
                if (d?.lowAttendanceThreshold !== null && d?.lowAttendanceThreshold !== undefined) {
                    setLowThreshold(d.lowAttendanceThreshold);
                }
            })
            .catch(() => {});
    }, [journal?.group?.id]);

    // п.6.1/п.7: посещаемость ученика только с даты присоединения (сравнение по календарной дате, TZ-safe)
    const getAttendance = (studentId) => {
        const jd = joinDates[studentId]; // 'YYYY-MM-DD' | undefined
        const dated = (journal?.lessons || []).filter(l => l.date && (!jd || String(l.date).slice(0, 10) >= jd));
        const total = dated.length;
        const present = dated.reduce((sum, lesson) => {
            const att = lesson.attendances?.find(a => a.user.id === studentId);
            return sum + (att?.isPresent ? 1 : 0);
        }, 0);
        return {total, present, percent: total ? (present * 100) / total : 0};
    };

    // п.6.1: задать/снять дату проведения задания (секции). Задания с датой раньше даты
    // присоединения ученика не учитываются в его итоговой оценке (см. beforeJoin в saveGrades).
    const setAssignmentDate = async (sectionId, date) => {
        try {
            await JournalService.setGradeSectionDate(sectionId, date || '');
        } catch {
            message.error(<IntlMessage id="error"/>, 3);
        } finally {
            dispatch(getJournal({id}));
        }
    };


    // Установка начальных значений для формы
    useEffect(() => {
        if (!journal) return;

        const initialValues = {};
        journal.grades?.forEach(grade => {
            initialValues[`grade-${grade.id}`] = grade.grade;
        });

        journal.projects?.forEach(project => {
            project.grades?.forEach(pGrade => {
                initialValues[`project-${pGrade.id}`] = pGrade.grade;
            });
        });

        journal.tests?.forEach(test => {
            test.grades?.forEach(tGrade => {
                initialValues[`test-${tGrade.id}`] = tGrade.grade;
            });
        });

        journal.moduleTests?.forEach(test => {
            test.grades?.forEach(tGrade => {
                initialValues[`moduleTest-${tGrade.id}`] = tGrade.grade;
            });
        });

        journal.finalTests?.forEach(finalTest => {
            initialValues[`finalTest-${finalTest.id}`] = finalTest.grade;
        });

        form.setFieldsValue(initialValues);
    }, [journal]);

    useEffect(() => {
        if (!journal) return;

        // ТЗ updated п.10: единый алфавитный порядок студентов (в БД порядок не определён)
        const sortedStudents = [...(journal?.group?.students || [])].sort((a, b) =>
            (a?.profile?.fullName || '').localeCompare(b?.profile?.fullName || '', 'kk')
        );
        setDataSource(
            sortedStudents.map((student, index) => {
                const gradeObj = journal.grades?.find(g => g.user?.id === student.id);
                const finalTestGrade = journal.finalTests?.find(g => g.user?.id === student.id);
                const base = {
                    key: String(index + 1),
                    name: student?.profile?.fullName || '',
                    studentId: student.id,
                    gradeId: gradeObj?.id,
                    grade: gradeObj?.grade ?? 0,
                    finalTestGrade: finalTestGrade?.grade ?? 0,
                    finalTestId: finalTestGrade?.id,
                    tests: [],
                    projects: [],
                };

                journal.projects?.forEach(project => {
                    const studentProjectGrade = project.grades?.find(g => g.user?.id === student.id);
                    base.projects.push(
                        {
                            id: studentProjectGrade?.id,
                            grade: studentProjectGrade?.grade ?? 0,
                            projectId: project.id
                        }
                    )
                });

                journal.tests?.forEach(test => {
                    const studentTestGrade = test.grades?.find(g => g.user?.id === student.id);
                    base.tests.push(
                        {
                            id: studentTestGrade?.id,
                            grade: studentTestGrade?.grade ?? 0,
                            testId: test.id
                        }
                    )
                });

                // Добавляем модульные тесты в массив tests
                journal.moduleTests?.forEach(test => {
                    const studentTestGrade = test.grades?.find(g => g.user?.id === student.id);
                    base.tests.push(
                        {
                            id: studentTestGrade?.id,
                            grade: studentTestGrade?.grade ?? 0,
                            testId: test.id
                        }
                    )
                });

                return base;
            })
        )
    }, [journal]);

    // Базовая колонка с ФИО
    const baseColumn = {
        title: <MyText center><IntlMessage id={"fullName"}/></MyText>,
        width: 200,
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        // п.10: клик по ФИО открывает расписание; п.7: подсветка низкой посещаемости
        render: (_, record) => {
            const att = getAttendance(record.studentId);
            const low = att.total > 0 && att.percent < lowThreshold;
            return (
                <span
                    style={{cursor: 'pointer', color: low ? '#cf1322' : '#1677ff', fontWeight: low ? 600 : 400}}
                    title={low ? `Низкая посещаемость: ${Math.round(att.percent)}%` : undefined}
                    onClick={() => {
                        setScheduleStudent({id: record.studentId, name: record.name});
                        setOpenSchedule(true);
                    }}
                >
                    {low ? '⚠️ ' : ''}{record.name}
                </span>
            );
        },
    };

    // Функция для извлечения уровня из названия (Level 1, Level 2, Level 3)
    const extractLevel = (name) => {
        const match = name.match(/Level (\d+)/i);
        return match ? parseInt(match[1]) : 999; // 999 для элементов без уровня
    };

    // Создаем и сортируем колонки по уровням
    const projectColumns = journal?.projects?.map(project => ({
        title: (
            <Flex vertical align={"center"} justify={"center"} gap={2}>
                <MyText center>{project.name} {" (" + project.maxGrade + ")"}</MyText>
                <input type="date" value={project.assignmentDate ? String(project.assignmentDate).slice(0, 10) : ''}
                       onChange={(e) => setAssignmentDate(project.id, e.target.value)}
                       title="Дата задания (учёт даты присоединения ученика)" style={{fontSize: 11}}/>
            </Flex>
        ),
        key: `project-${project.id}`,
        width: 150,
        level: extractLevel(project.name),
        type: 'project',
        render: (record) => (
            <FormItem
                name={`project-${record?.projects.find(pr => pr.projectId === project.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.projects.find(pr => pr.testId === project.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    const testColumns = journal?.tests?.map(test => ({
        title: (
            <Flex vertical align={"center"} justify={"center"} gap={2}>
                <MyText center>{test.name} {" (" + test.maxGrade + ")"}</MyText>
                <input type="date" value={test.assignmentDate ? String(test.assignmentDate).slice(0, 10) : ''}
                       onChange={(e) => setAssignmentDate(test.id, e.target.value)}
                       title="Дата задания (учёт даты присоединения ученика)" style={{fontSize: 11}}/>
            </Flex>
        ),
        key: `test-${test.id}`,
        width: 150,
        level: extractLevel(test.name),
        type: 'test',
        render: (record) => (
            <FormItem
                name={`test-${record?.tests.find(t => t.testId === test.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.tests.find(t => t.testId === test.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                {/*{console.log(record)}*/}
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    const moduleTestsColumns = journal?.moduleTests?.map(test => ({
        title: (
            <Flex vertical align={"center"} justify={"center"} gap={2}>
                <MyText center>{test.name} {" (" + test.maxGrade + ")"}</MyText>
                <input type="date" value={test.assignmentDate ? String(test.assignmentDate).slice(0, 10) : ''}
                       onChange={(e) => setAssignmentDate(test.id, e.target.value)}
                       title="Дата задания (учёт даты присоединения ученика)" style={{fontSize: 11}}/>
            </Flex>
        ),
        key: `moduleTest-${test.id}`,
        width: 150,
        level: extractLevel(test.name),
        type: 'moduleTest',
        render: (record) => (
            <FormItem
                name={`moduleTest-${record?.tests.find(t => t.testId === test.id)?.id}`}
                style={{marginBottom: 0}}
                initialValue={record?.tests.find(t => t.testId === test.id)?.grade}
                rules={[{required: true, message: <IntlMessage id={"tg.emptyInput"}/>}]}
            >
                {/*{console.log(record)}*/}
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    })).sort((a, b) => a.level - b.level) || [];

    // Объединяем и сортируем колонки по уровням
    const sortedColumns = [...projectColumns, ...testColumns, ...moduleTestsColumns].sort((a, b) => {
        // Сначала по уровню
        if (a.level !== b.level) {
            return a.level - b.level;
        }
        // Внутри одного уровня: проекты, тесты, модульные тесты
        const typeOrder = {project: 0, test: 1, moduleTest: 2};
        return typeOrder[a.type] - typeOrder[b.type];
    });

    const shouldShowFinalTest = journal?.group?.students?.some(student => {
        const finalTestGrade = journal.finalTests?.find(g => g.user?.id === student.id);
        return finalTestGrade?.grade !== undefined && finalTestGrade?.grade !== null;
    });

    const isMathCourse = journal?.group?.category?.nameEng === 'Mathematics'

    const finalTestColumn = {
        title: <MyText center><IntlMessage id={"tg.finalGrade"}/></MyText>,
        width: 150,
        key: 'finalTest',
        render: (_, record) => (
            <FormItem
                name={`finalTest-${record.finalTestId}`}
                style={{marginBottom: 0}}
                initialValue={record.finalTestGrade}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const finalTestColumnForMath = {
        title: <MyText center><IntlMessage id={"tg.finalGrade"}/></MyText>,
        width: 150,
        key: 'finalTest',
        render: (_, record) => (
            <FormItem
                name={`finalTest-${record.studentId}`}
                style={{marginBottom: 0}}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const gradeColumn = {
        title: <MyText center><IntlMessage id={"tg.grade"}/></MyText>,
        width: 150,
        dataIndex: 'grade',
        key: 'grade',
        fixed: 'right',
        render: (_, record) => (
            <FormItem
                name={`grade-${record.gradeId}`}
                style={{marginBottom: 0}}
                initialValue={record.grade}
                rules={[
                    {
                        required: true,
                        message: <IntlMessage id={"tg.emptyInput"}/>,
                    }
                ]}
            >
                <InputNumber min={0} max={100} step={1} style={{width: "100%"}}/>
            </FormItem>
        ),
    };

    const columns = [baseColumn, ...sortedColumns, ...(shouldShowFinalTest ? [finalTestColumn] : (isMathCourse ? [finalTestColumnForMath] : [])), gradeColumn];

    const saveGrades = async () => {
        const values = form.getFieldsValue();
        const gradeMap = {};
        const projectGradeMap = {};
        const testGradeMap = {};
        const moduleTestGradeMap = {};
        const finalTestGradeMap = {};

        for (const [key, value] of Object.entries(values)) {
            if (key.startsWith('project-')) {
                const id = key.replace('project-', '');
                projectGradeMap[id] = value;
            }
            if (key.startsWith('test-')) {
                const id = key.replace('test-', '');
                testGradeMap[id] = value;
            }
            if (key.startsWith('moduleTest-')) {
                const id = key.replace('moduleTest-', '');
                moduleTestGradeMap[id] = value;
            }
            if (key.startsWith('finalTest-')) {
                const id = key.replace('finalTest-', '');
                finalTestGradeMap[id] = value;
            }
        }

        // Обработка итоговой оценки для General English
        if (journal?.group?.category?.nameEng === 'General English') {
            journal?.group?.students?.forEach(student => {
                // const studentTests = journal.tests?.flatMap(test =>
                //     test.grades?.filter(g => g.user.id === student.id) || []
                // );
                //
                // const testSum = studentTests.reduce((sum, g) => sum + (g.grade ?? 0), 0);
                // const avgTest = testSum / studentTests.length;
                const studentTestGrades = journal.tests?.flatMap(test =>
                    test.grades?.filter(g => g.user.id === student.id).map(g => g.id) || []
                );

                const testValues = studentTestGrades.map(id => testGradeMap[id] ?? 0);
                const testSum = testValues.reduce((sum, val) => sum + val, 0);

                // п.6.1: посещаемость считаем только с даты присоединения ученика
                const attendancePercent = getAttendance(student.id).percent;
                const attendanceScore = attendancePercent * 0.4;
                const finalGrade = (testSum * 0.6) + attendanceScore;
                const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                if (gradeObj?.id) {
                    gradeMap[gradeObj.id] = Math.round(finalGrade);
                }
            });
        } else if (journal?.group?.category?.nameEng === 'Mathematics') {
            journal?.group?.students?.forEach(student => {
                const finalTestValue = finalTestGradeMap[student.id] ?? 0;
                // п.6.1: посещаемость считаем только с даты присоединения ученика
                const attendancePercent = getAttendance(student.id).percent;
                const attendanceScore = attendancePercent * 0.6;

                const finalGrade = (finalTestValue * 0.4) + attendanceScore;
                const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                if (gradeObj?.id) {
                    gradeMap[gradeObj.id] = Math.round(finalGrade);
                }
            });
        } else {
            if (journal?.group?.category?.nameEng === 'Foundation' && journal?.group?.course?.nameEng === 'General English') {
                // п.6.1: дата проведения задания по секции (assignmentDate с бэка) — задания ДО присоединения
                // ученика не учитываем в итоговой оценке. п.6.2: значения уже в 100-балльной (ученик вводит 0..100),
                // поэтому берём взвешенное среднее без деления на maxGrade; guard от деления на ноль (NaN).
                const sectionDate = {};
                [...(journal?.projects || []), ...(journal?.tests || []), ...(journal?.moduleTests || [])]
                    .forEach(section => (section.grades || []).forEach(g => {
                        if (g?.id != null) sectionDate[g.id] = section.assignmentDate;
                    }));
                const beforeJoin = (gradeId, studentId) => {
                    const d = sectionDate[gradeId];
                    const jd = joinDates[studentId];
                    return !!(d && jd && String(d).slice(0, 10) < jd);
                };
                const avg = (arr) => arr.length ? arr.reduce((s, v) => s + (Number(v) || 0), 0) / arr.length : 0;

                journal?.group?.students?.forEach(student => {
                    // Тесты: 60%
                    const testIds = (journal.tests || []).flatMap(test =>
                        (test.grades || []).filter(g => g.user.id === student.id).map(g => g.id))
                        .filter(id => !beforeJoin(id, student.id));
                    const testScore = avg(testIds.map(id => testGradeMap[id] ?? 0)) * 0.6;

                    // Проекты: 20%
                    const projectIds = (journal.projects || []).flatMap(project =>
                        (project.grades || []).filter(g => g.user.id === student.id).map(g => g.id))
                        .filter(id => !beforeJoin(id, student.id));
                    const projectScore = avg(projectIds.map(id => projectGradeMap[id] ?? 0)) * 0.2;

                    // Модульные тесты + финальный тест: 20%
                    const moduleIds = (journal.moduleTests || []).flatMap(test =>
                        (test.grades || []).filter(g => g.user.id === student.id).map(g => g.id))
                        .filter(id => !beforeJoin(id, student.id));
                    const moduleValues = moduleIds.map(id => moduleTestGradeMap[id] ?? 0);
                    const studentFinalTestGrade = journal.finalTests?.find(g => g.user.id === student.id)?.grade;
                    const moduleFinalTestValues = studentFinalTestGrade != null ? [...moduleValues, studentFinalTestGrade] : moduleValues;
                    const moduleTestScore = avg(moduleFinalTestValues) * 0.2;

                    const finalGrade = testScore + projectScore + moduleTestScore;
                    const gradeObj = journal.grades?.find(g => g.user.id === student.id);
                    if (gradeObj?.id) {
                        gradeMap[gradeObj.id] = Math.round(finalGrade);
                    }
                });
            } else {
                // Для остальных категорий и курсов
                for (const [key, value] of Object.entries(values)) {
                    if (key.startsWith('grade-')) {
                        const id = key.replace('grade-', '');
                        gradeMap[id] = value;
                    }
                }
            }

        }

        try {
            const isMathCategory = journal?.group?.category?.nameEng === 'Mathematics';

            const saveGradeTasks = isMathCategory
                ? [
                    JournalService.saveGrades(gradeMap),
                    JournalService.saveGrades(finalTestGradeMap)
                ]
                : [
                    JournalService.saveGrades(projectGradeMap),
                    JournalService.saveGrades(testGradeMap),
                    JournalService.saveGrades(moduleTestGradeMap),
                    JournalService.saveGrades(finalTestGradeMap),
                    JournalService.saveGrades(gradeMap),
                ];

            await Promise.all(saveGradeTasks);

            message.success(<IntlMessage id="success"/>, 5);
        } catch {
            message.error(<IntlMessage id="error"/>, 5);
        } finally {
            form.resetFields();
            dispatch(getJournal({id}));
        }
    };


    return (
        <Flex style={{width: '100%'}} gap={20} vertical>
            <BlockLoading isLoading={isLoading}/>
            <Form form={form}>
                <Table
                    bordered
                    pagination={false}
                    columns={columns}
                    dataSource={dataSource}
                    scroll={{x: 'max-content'}}
                    size="small"
                />
            </Form>
            <Flex gap={10}>
                {/* п.6.2: убрана ручная кнопка «перевод в 100-балльную» — она делила уже-100-балльные оценки
                    на maxGrade и портила данные. Итоговая оценка теперь корректно считается по введённым 0..100. */}
                <Button type="primary" onClick={saveGrades}>
                    <IntlMessage id={'save'}/>
                </Button>
            </Flex>
            <StudentScheduleModal
                open={openSchedule}
                onClose={() => setOpenSchedule(false)}
                studentId={scheduleStudent?.id}
                studentName={scheduleStudent?.name}
            />
        </Flex>
    );
};

export default GradesPage;
